import { ApiError } from "../api";
import type { MapRuntimeError } from "../map/runtime";
import { telemetryClient, type TelemetryDeduper } from "./client";
import type {
  TelemetryContext,
  TelemetryEventName,
  TelemetryFailureKind,
  TelemetryFlightMode
} from "./schema";
import { telemetryEventSignature } from "./schema";

export function telemetryFlightMode(mode: "free_flight" | "ppg"): TelemetryFlightMode {
  return mode === "ppg" ? "ppg" : "pg";
}

export function telemetryFailureKindFromApiReason(
  reason: unknown
): TelemetryFailureKind | null {
  if (reason instanceof ApiError) {
    if (reason.kind === "aborted") {
      return null;
    }
    return reason.kind;
  }
  if (
    typeof DOMException !== "undefined" &&
    reason instanceof DOMException &&
    reason.name === "AbortError"
  ) {
    return null;
  }
  if (reason instanceof Error && /timeout/i.test(reason.message)) {
    return "timeout";
  }
  return reason instanceof Error ? "runtime" : null;
}

export function telemetryFailureKindFromMapError(
  error: MapRuntimeError
): TelemetryFailureKind {
  switch (error.category) {
    case "unsupported-webgl":
      return "unsupported_webgl";
    case "constructor":
      return "constructor";
    case "worker":
      return "worker";
    case "tile":
      return "tile";
    case "render":
    case "runtime":
    default:
      return "runtime";
  }
}

export function emitTelemetryEvent(
  event: TelemetryEventName,
  context: Omit<TelemetryContext, "release_version" | "release_channel"> &
    Partial<Pick<TelemetryContext, "release_version" | "release_channel">>,
  options?: {
    deduper?: TelemetryDeduper;
    fetcher?: typeof fetch;
  }
): Promise<boolean> {
  const payload = telemetryClient.prepare(event, context);
  if (payload === null) {
    return Promise.resolve(false);
  }
  return telemetryClient.post(payload, options);
}

export function telemetrySignatureForEvent(
  event: TelemetryEventName,
  context: Omit<TelemetryContext, "release_version" | "release_channel"> &
    Partial<Pick<TelemetryContext, "release_version" | "release_channel">>
): string | null {
  const payload = telemetryClient.prepare(event, context);
  return payload === null ? null : telemetryEventSignature(payload);
}
